import { useLocalization } from "@fluent/react";
import { LoadingEllipses } from "cerulean";
import { useEffect } from "react";
import { NavigationType, useBlocker } from "react-router";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { StatusCard } from "@lending/components/StatusTimeline";
import { useGetVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/useGetVehicleLoanApplicationQuery";
import { useCreditPullTimeout } from "@lending/hooks/useCreditPullTimeout";
import { useFlowNavigation } from "@lending/navigation";
import styles from "./CreditPull.module.css";

function CreditPull() {
  const { l10n } = useLocalization();
  const { goToStep } = useFlowNavigation();

  const { application, phase } = useCreditPullTimeout(useGetVehicleLoanApplicationQuery);
  const applicationState = application?.state;
  const isTimedOut = phase === "timeout";
  const isSlow = phase === "slow";

  useEffect(() => {
    if (!applicationState) {
      return;
    }

    switch (applicationState) {
      case "creating":
      case "decisioning_queued":
      case "decisioning_processing":
        return;
      case "offers_generated":
        return goToStep("approved");
      default:
        return goToStep("submitted");
    }
  }, [goToStep, applicationState]);

  useBlocker((navigation) => {
    return navigation.historyAction === NavigationType.Pop;
  });

  if (isTimedOut) {
    return (
      <PageLayout className={styles.timeoutPageLayout}>
        <div className={styles.timeoutContainer}>
          <div className={styles.timeoutContent}>
            <StatusCard
              title={l10n.getString(
                "credit-pull-timeout-title",
                null,
                "We received your application"
              )}
              description={l10n.getString(
                "credit-pull-timeout-message-1",
                null,
                "A loan officer will reach out to complete processing your application."
              )}
            />
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout style={{ paddingTop: 0 }}>
      <div className="alignChild--center--center" style={{ height: "100%" }}>
        <div>
          <div
            className="alignChild--center--center margin--bottom--xl"
            style={{ position: "relative" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="150"
              height="150"
              viewBox="0 0 150 150"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M70.6645 0.0195305C106.349 -0.78658 138.977 23.4409 148.334 57.8864C156.608 88.3471 132.703 114.826 106.721 132.75C83.1586 149.004 52.9328 156.214 29.3807 139.945C4.19928 122.55 -5.56762 90.5215 3.09307 61.1673C12.5968 28.9559 37.0889 0.777998 70.6645 0.0195305Z"
                fill="var(--theme-primary)"
                fillOpacity="0.2"
              />
            </svg>
            <div className={styles.loadingEllipses}>
              <LoadingEllipses />
            </div>
          </div>
          <div className="alignChild--center--center" style={{ flexDirection: "column" }}>
            {isSlow ? (
              <>
                <div className="fontWeight--semibold fontSize--l margin--bottom--xl">
                  {l10n.getString("credit-pull-slow-title", null, "Still working on it...")}
                </div>
                <div>
                  {l10n.getString(
                    "credit-pull-slow-message-1",
                    null,
                    "This is taking a little longer than usual —"
                  )}
                </div>
                <div>
                  {l10n.getString(
                    "credit-pull-slow-message-2",
                    null,
                    "please do not close this window."
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="fontWeight--semibold fontSize--l margin--bottom--xl">
                  {l10n.getString("credit-pull-title", null, "Finding the best rate...")}
                </div>
                <div>
                  {l10n.getString("credit-pull-message-1", null, "This can take up to a minute —")}
                </div>
                <div>
                  {l10n.getString(
                    "credit-pull-message-2",
                    null,
                    "please do not close this window."
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export { CreditPull };
